import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEditor } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FileText, Plus, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ModulePage({ section, title, subtitle, icon }) {
  const { user } = useAuth();
  const editor = isEditor(user?.role);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", tags: "" });

  const load = useCallback(() => api.get("/articles-filter", { params: { section } }).then((r) => {
    setItems(r.data); setActive((prev) => prev ? r.data.find((x) => x.id === prev.id) || r.data[0] : r.data[0]);
  }).catch((e) => console.error("articles load", e)), [section]);
  useEffect(() => { setActive(null); load(); }, [load]);

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    try {
      await api.post("/articles", { section, title: form.title, body: form.body, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) });
      toast.success("Saved"); setOpen(false); setForm({ title: "", body: "", tags: "" }); load();
    } catch { toast.error("Could not save"); }
  };
  const remove = async (id) => { await api.delete(`/articles/${id}`); setActive(null); load(); toast.success("Deleted"); };

  return (
    <div data-testid={`module-${section}`}>
      <PageHeader overline="Firm Knowledge Base" title={title} subtitle={subtitle}>
        {editor && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="module-add-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Entry</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>New {title} entry</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Title</Label><Input data-testid="module-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label className="text-xs">Body</Label><Textarea data-testid="module-body-input" rows={9} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
                <div><Label className="text-xs">Tags (comma separated)</Label><Input data-testid="module-tags-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="module-save-btn" onClick={save}>Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState icon={FileText} title="No entries yet" hint={editor ? "Add the first entry using the button above." : undefined} />
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="space-y-2" data-testid="module-list">
            {items.map((a) => (
              <button key={a.id} onClick={() => setActive(a)} data-testid="module-item"
                className={`w-full text-left flex items-center gap-3 border rounded-sm p-3.5 transition-colors ${active?.id === a.id ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/60"}`}>
                <FileText className="h-4 w-4 text-primary shrink-0" />
                <span className="flex-1 min-w-0 text-sm font-medium truncate">{a.title}</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>
          <div className="lg:col-span-2">
            {active && (
              <div className="border border-border bg-card rounded-sm p-6 animate-fade-up" data-testid="module-detail">
                <div className="flex items-start justify-between gap-4 mb-4 pb-4 border-b border-border">
                  <div>
                    <h2 className="font-display font-bold text-xl">{active.title}</h2>
                    {active.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {active.tags.map((t) => <span key={t} className="text-[11px] px-1.5 py-0.5 bg-secondary text-muted-foreground rounded-sm">{t}</span>)}
                      </div>
                    )}
                  </div>
                  {editor && <button data-testid="module-delete-btn" onClick={() => remove(active.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
                </div>
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground/90">{active.body}</pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
